import { type DeviceInfo, getOrientation } from "shared/types/DeviceInfo";

/** A window's size in CSS pixels. */
export type ViewportSize = {
  /** `innerWidth` of the window, in CSS pixels. */
  width: number;
  /** `innerHeight` of the window, in CSS pixels. */
  height: number;
};

type GetScreenInfoProps = {
  /** This window's `innerWidth`/`innerHeight`. */
  window: ViewportSize;
  /**
   * Whether the app runs inside the SSO iframe. The parent stretches that
   * iframe to the full height of its content and does the scrolling itself, so
   * the iframe's own height is the page length, not the user's screen.
   */
  embedded?: boolean;
  /**
   * The parent page's window size, when the iframe wrapper reports it. Older
   * copies of `iframe.html` pasted into a society's site do not.
   */
  parentViewport?: ViewportSize;
};

/**
 * The screen measurements `DeviceInfo` carries.
 *
 * - Not embedded: this window's size and orientation.
 * - Embedded: the width is still the iframe's own, which is what the hub's CSS
 *   media queries see. Height and orientation come from the parent's window
 *   when the wrapper reports it, and are left out otherwise - the stretched
 *   iframe height would make every Max Height fail and every screen portrait,
 *   while a missing measurement is treated as "constraint met".
 */
export const getScreenInfo = ({
  window,
  embedded = false,
  parentViewport,
}: GetScreenInfoProps): Pick<
  DeviceInfo,
  "width" | "height" | "orientation"
> => {
  if (!embedded) {
    return {
      width: window.width,
      height: window.height,
      orientation: getOrientation(window),
    };
  }
  if (!parentViewport) {
    return { width: window.width };
  }
  return {
    width: window.width,
    height: parentViewport.height,
    orientation: getOrientation(parentViewport),
  };
};

/**
 * Reads a parent viewport size posted by the iframe wrapper. Returns
 * `undefined` for anything that is not two positive numbers, which is what an
 * older wrapper that never sends the fields produces.
 */
export const parseViewportSize = ({
  width,
  height,
}: {
  /** The posted `viewportWidth`. */
  width: unknown;
  /** The posted `viewportHeight`. */
  height: unknown;
}): ViewportSize | undefined => {
  const parsedWidth = Number(width);
  const parsedHeight = Number(height);
  if (
    width === undefined ||
    height === undefined ||
    !(parsedWidth > 0) ||
    !(parsedHeight > 0)
  ) {
    return undefined;
  }
  return { width: parsedWidth, height: parsedHeight };
};
